import { useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { X, Paperclip, Upload, Trash2, FileText } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useLookups, useCustomFields } from "@/lib/lookups";
import { useToast, Progress } from "@/components/ui";
import { HealthBadge, StatusBadge, PriorityDot } from "@/components/badges";
import { Field, TextInput, TextArea, Select, UserPicker, CustomFieldInput } from "@/components/fields";
import { fmtDate, fmtDateTime, money, pct } from "@/lib/format";
import type { Activity } from "@shared/types";
import type { ProjectBundle } from "../ProjectDetail";
import { useActivityMutations } from "./useActivities";

export function ActivityDrawer({ activity, bundle, onClose }: {
  activity: Activity; bundle: ProjectBundle; onClose: () => void;
}) {
  const { user, can } = useAuth();
  const toast = useToast();
  const { byCategory } = useLookups();
  const customDefs = useCustomFields("activity");
  const m = useActivityMutations(bundle.project.id);
  const isAssignee = activity.responsibleUserId === user?.id;
  const canManage = bundle.access.canManageActivities;
  const canStatus = canManage || (isAssignee && can("activities.update_status"));

  const [tab, setTab] = useState<"detail" | "evidence">("detail");
  const [form, setForm] = useState(() => ({ ...activity }));
  const [custom, setCustom] = useState<Record<string, unknown>>(activity.custom || {});
  useEffect(() => { setForm({ ...activity }); setCustom(activity.custom || {}); }, [activity]);

  const detail = useQuery({
    queryKey: ["activity-detail", activity.id],
    queryFn: () => api.get<{ activity: Activity; attachments: any[] }>(`/activities/${activity.id}`),
  });

  const saveField = (patch: Partial<Activity>) => {
    if (canManage) m.update.mutate({ id: activity.id, ...patch }, { onSuccess: () => toast.push("success", "Saved") });
  };
  const saveStatus = (patch: { status?: string; percentComplete?: number; note?: string }) => {
    m.status.mutate({ id: activity.id, ...patch }, { onSuccess: () => toast.push("success", "Status updated") });
  };
  const saveCustom = () => m.custom.mutate({ id: activity.id, values: custom }, { onSuccess: () => toast.push("success", "Fields saved") });

  return (
    <div className="fixed inset-0 z-40 flex justify-end">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative flex h-full w-full max-w-xl flex-col bg-surface shadow-pop animate-fade-in">
        <div className="flex items-start justify-between gap-2 border-b border-line px-5 py-3.5">
          <div className="min-w-0">
            <p className="text-[11px] uppercase tracking-wide text-ink-faint">{activity.phaseName || "Unassigned phase"}</p>
            <h3 className="truncate font-serif text-lg font-semibold">{activity.title}</h3>
            <div className="mt-1 flex flex-wrap items-center gap-2">
              <HealthBadge health={activity.scheduleHealth} />
              <StatusBadge value={activity.status} />
              <PriorityDot value={activity.priority} />
            </div>
          </div>
          <button className="btn-ghost -mr-2 !p-1.5" onClick={onClose}><X size={18} /></button>
        </div>

        <div className="flex gap-1 border-b border-line px-4">
          {(["detail", "evidence"] as const).map((t) => (
            <button key={t} onClick={() => setTab(t)}
              className={`border-b-2 px-3 py-2 text-sm font-medium capitalize ${tab === t ? "border-brand-600 text-brand-700" : "border-transparent text-ink-soft"}`}>
              {t === "evidence" ? `Evidence (${activity.attachmentCount})` : "Details"}
            </button>
          ))}
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4">
          {tab === "detail" ? (
            <div className="space-y-4">
              {/* Progress / status — available to assignee + manager */}
              <div className="card p-3">
                <p className="label">Status &amp; progress</p>
                {canStatus ? (
                  <>
                    <div className="flex gap-2">
                      <Select value={form.status} onChange={(e) => { setForm({ ...form, status: e.target.value }); saveStatus({ status: e.target.value }); }}>
                        {byCategory("status").map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
                      </Select>
                    </div>
                    <div className="mt-3 flex items-center gap-3">
                      <input type="range" min={0} max={100} step={5} value={form.percentComplete}
                        onChange={(e) => setForm({ ...form, percentComplete: Number(e.target.value) })}
                        onMouseUp={() => saveStatus({ percentComplete: form.percentComplete })}
                        onTouchEnd={() => saveStatus({ percentComplete: form.percentComplete })}
                        className="flex-1" />
                      <span className="w-10 text-right text-sm font-semibold tabular-nums">{pct(form.percentComplete)}</span>
                    </div>
                    <textarea placeholder="Add a progress note (optional)…" className="input mt-2 min-h-[54px] text-sm"
                      onBlur={(e) => { if (e.target.value.trim()) { saveStatus({ note: e.target.value.trim() }); e.target.value = ""; } }} />
                  </>
                ) : (
                  <div className="flex items-center gap-3">
                    <Progress value={activity.percentComplete} className="flex-1" />
                    <span className="text-sm font-semibold">{pct(activity.percentComplete)}</span>
                  </div>
                )}
              </div>

              <div className="grid grid-cols-2 gap-3">
                <ReadOrEdit label="Start date" edit={canManage}
                  view={fmtDate(activity.startDate)}
                  control={<TextInput type="date" value={form.startDate ?? ""} onChange={(e) => setForm({ ...form, startDate: e.target.value })} onBlur={() => saveField({ startDate: form.startDate })} />} />
                <ReadOrEdit label="End date" edit={canManage}
                  view={fmtDate(activity.endDate)}
                  control={<TextInput type="date" value={form.endDate ?? ""} onChange={(e) => setForm({ ...form, endDate: e.target.value })} onBlur={() => saveField({ endDate: form.endDate })} />} />
                <ReadOrEdit label="Weightage" edit={canManage}
                  view={activity.weightage}
                  control={<TextInput type="number" step="0.01" value={form.weightage} onChange={(e) => setForm({ ...form, weightage: Number(e.target.value) })} onBlur={() => saveField({ weightage: form.weightage })} />} />
                <ReadOrEdit label="Priority" edit={canManage}
                  view={activity.priority}
                  control={<Select value={form.priority} onChange={(e) => { setForm({ ...form, priority: e.target.value }); saveField({ priority: e.target.value }); }}>
                    {byCategory("priority").map((pr) => <option key={pr.value} value={pr.value}>{pr.label}</option>)}
                  </Select>} />
                <ReadOrEdit label="Planned cost" edit={canManage}
                  view={money(activity.plannedCost, bundle.project.currency)}
                  control={<TextInput type="number" value={form.plannedCost} onChange={(e) => setForm({ ...form, plannedCost: Number(e.target.value) })} onBlur={() => saveField({ plannedCost: form.plannedCost })} />} />
                <ReadOrEdit label="Actual cost" edit={canManage || isAssignee}
                  view={money(activity.actualCost, bundle.project.currency)}
                  control={<TextInput type="number" value={form.actualCost} onChange={(e) => setForm({ ...form, actualCost: Number(e.target.value) })} onBlur={() => saveField({ actualCost: form.actualCost })} />} />
              </div>

              <Field label="Responsible person">
                {canManage
                  ? <UserPicker value={form.responsibleUserId} onChange={(v) => { setForm({ ...form, responsibleUserId: v }); saveField({ responsibleUserId: v }); }} />
                  : <p className="text-sm">{activity.responsibleUserName || activity.responsibleRole || "—"}</p>}
              </Field>
              {canManage && (
                <Field label="Responsible role">
                  <Select value={form.responsibleRole ?? ""} onChange={(e) => { setForm({ ...form, responsibleRole: e.target.value }); saveField({ responsibleRole: e.target.value }); }}>
                    <option value="">—</option>
                    {byCategory("responsible_role").map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
                  </Select>
                </Field>
              )}

              <Field label="Description">
                {canManage
                  ? <TextArea value={form.description ?? ""} onChange={(e) => setForm({ ...form, description: e.target.value })} onBlur={() => saveField({ description: form.description })} />
                  : <p className="whitespace-pre-wrap text-sm text-ink-soft">{activity.description || "—"}</p>}
              </Field>
              <Field label="Notes / risks / issues">
                {canManage
                  ? <TextArea value={form.notes ?? ""} onChange={(e) => setForm({ ...form, notes: e.target.value })} onBlur={() => saveField({ notes: form.notes })} />
                  : <p className="whitespace-pre-wrap text-sm text-ink-soft">{activity.notes || "—"}</p>}
              </Field>

              {customDefs.length > 0 && (
                <div className="card p-3">
                  <p className="label">Custom fields</p>
                  <div className="space-y-3">
                    {customDefs.map((d) => (
                      <Field key={d.id} label={d.label} hint={d.helpText || undefined}>
                        <CustomFieldInput def={d} value={custom[d.key]} onChange={(v) => setCustom({ ...custom, [d.key]: v })} />
                      </Field>
                    ))}
                  </div>
                  <button className="btn-outline mt-3 text-xs" onClick={saveCustom}>Save custom fields</button>
                </div>
              )}

              <p className="text-[11px] text-ink-faint">
                Created {fmtDateTime(activity.createdAt)} · Updated {fmtDateTime(activity.updatedAt)}
              </p>
            </div>
          ) : (
            <EvidencePanel activity={activity} bundle={bundle} attachments={detail.data?.attachments ?? []} onChanged={() => detail.refetch()} />
          )}
        </div>
      </div>
    </div>
  );
}

function ReadOrEdit({ label, view, control, edit }: { label: string; view: React.ReactNode; control: React.ReactNode; edit: boolean }) {
  return (
    <div>
      <span className="label">{label}</span>
      {edit ? control : <p className="text-sm">{view}</p>}
    </div>
  );
}

function EvidencePanel({ activity, bundle, attachments, onChanged }: {
  activity: Activity; bundle: ProjectBundle; attachments: any[]; onChanged: () => void;
}) {
  const { user, can } = useAuth();
  const toast = useToast();
  const fileRef = useRef<HTMLInputElement>(null);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const canUpload = can("activities.attach_evidence") && (bundle.access.canManageActivities || activity.responsibleUserId === user?.id);

  const upload = async (file: File) => {
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("projectId", bundle.project.id);
      fd.append("activityId", activity.id);
      fd.append("kind", "evidence");
      if (note) fd.append("note", note);
      await api.upload("/attachments", fd);
      setNote("");
      toast.push("success", "Evidence uploaded");
      onChanged();
    } catch (e: any) {
      toast.push("error", e?.message || "Upload failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-3">
      {canUpload && (
        <div className="card p-3">
          <p className="label">Attach supporting evidence</p>
          <TextInput placeholder="Short note about this evidence (optional)" value={note} onChange={(e) => setNote(e.target.value)} />
          <input ref={fileRef} type="file" className="hidden" onChange={(e) => e.target.files?.[0] && upload(e.target.files[0])} />
          <button className="btn-outline mt-2 w-full" disabled={busy} onClick={() => fileRef.current?.click()}>
            <Upload size={15} /> {busy ? "Uploading…" : "Choose file (max 25 MB)"}
          </button>
        </div>
      )}
      {attachments.length === 0 ? (
        <p className="py-6 text-center text-sm text-ink-faint"><Paperclip size={16} className="mx-auto mb-1" />No evidence attached yet.</p>
      ) : (
        <ul className="space-y-2">
          {attachments.map((a) => (
            <li key={a.id} className="flex items-start gap-3 rounded-lg border border-line p-2.5">
              <FileText size={18} className="mt-0.5 shrink-0 text-ink-faint" />
              <div className="min-w-0 flex-1">
                <a href={`${a.url}?dl=1`} className="block truncate text-sm font-medium hover:text-brand-600">{a.filename}</a>
                <p className="text-[11px] text-ink-faint">
                  {(a.sizeBytes / 1024).toFixed(0)} KB · {a.uploadedByName} · {fmtDateTime(a.createdAt)}
                  {a.statusAtUpload ? ` · status was ${a.statusAtUpload}` : ""}
                </p>
                {a.note && <p className="mt-0.5 text-xs text-ink-soft">{a.note}</p>}
              </div>
              {(bundle.access.canManageActivities || a.uploadedByName === user?.fullName) && (
                <button className="btn-ghost !p-1 text-st-delayed" onClick={async () => { await api.del(`/attachments/${a.id}`); onChanged(); }}>
                  <Trash2 size={14} />
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
